import React from "react";
import {
  Mail,
  CircleCheck,
  Eye,
  Upload,
  AlarmClock,
  File,
  Clock,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardDescription,
  CardContent,
  CardTitle,
} from "../ui/card";
import { SectionHeader } from "../ui/sectionHeader";
import { DotSeprated } from "../comp-utils/UploadUtils";
import { formatDateAndTimeFromISO, humanReadableDate, humanReadableTime } from "../../utils/dateHelper";
import PropTypes from "prop-types";
// Icon
export const IconType = ({ type, color }) => {
  switch (type) {
    case "completed":
      return <CircleCheck className={`text-[var(${color})]`} />;
    case "started":
      return <Mail className="text-[var(--color-system-info-2)]" />;
    case "reminder":
      return <AlarmClock className="text-[var(--color-system-warning-2)]" />;
    case "watching":
      return <Eye className="text-[var(--color-system-warning-2)]" />;
    case "upload":
      return <Upload className="text-[var(--color-system-info-2)]" />;
    case "file":
      return (
        <File
          className={`${
            color
              ? `text-[${color}]` || `text-[var(${color})]`
              : "text-[var(--color-system-info-2)]"
          }`}
        />
      );
    case "clock":
      return <Clock className="text-[var(--color-system-info-2)]" />;
    case "circle-filled":
      return <div className={`w-5 h-5 rounded-full bg-[var(${color})]`}></div>;
    case "circle":
      return null;
  }
};
const DEFAULT_STYLES = {
  topColor: "bg-[var(--color-neutral-5)]",
  bottomColor: "bg-[var(--color-neutral-5)]",
  iconDimensions: "w-10 h-10 min-h-10 min-w-10",
  topHeight: "h-12",
  resolvedType: "circle",
};

const getProgressStyles = (isCompleted, isDisabled, color, active) => {
  const resolvedType = (isCompleted || isDisabled) ? "circle-filled" : "circle";
  const iconBg = isCompleted
    ? `bg-transparent border-2 border-[var(${color})]`
    : "bg-transparent border-2 border-[var(--color-neutral-5)]";

  let topColor = DEFAULT_STYLES.topColor;
  let bottomColor = DEFAULT_STYLES.bottomColor;

  if (isCompleted) {
    topColor = `bg-[var(${color})]`;
    bottomColor = active ? DEFAULT_STYLES.bottomColor : `bg-[var(${color})]`;
  }

  return {
    iconDimensions: "w-8 h-8",
    topHeight: "h-2",
    resolvedType,
    iconBg,
    topColor,
    bottomColor,
  };
};

const getCardMargin = (isProgress, isFirst, isLast) => {
  if (isProgress) return isLast ? "mb-0" : "mb-10";
  return isFirst ? "mt-0" : "mt-6";
};

const getTopStyles = (isFirst, isProgress, currentTopColor) => {
  if (!isFirst) return { topColor: currentTopColor, topHeight: isProgress ? "h-2" : DEFAULT_STYLES.topHeight };
  return {
    topColor: "bg-transparent",
    topHeight: isProgress ? "h-2" : "h-6",
  };
};

const getTimelineItemStyles = ({
  isProgress,
  isCompleted,
  isDisabled,
  isFirst,
  isLast,
  active,
  color,
  iconBackgroundColor,
}) => {
  const baseIconBg = iconBackgroundColor ?? "bg-[var(--color-neutral-1)]";

  const progressStyles = isProgress
    ? getProgressStyles(isCompleted, isDisabled, color, active)
    : {
        iconDimensions: DEFAULT_STYLES.iconDimensions,
        topHeight: DEFAULT_STYLES.topHeight,
        resolvedType: DEFAULT_STYLES.resolvedType,
        iconBg: baseIconBg,
        topColor: DEFAULT_STYLES.topColor,
        bottomColor: DEFAULT_STYLES.bottomColor,
      };

  const { topColor: topColorFromProgress, topHeight: topHeightFromProgress } = progressStyles;
  const { topColor, topHeight } = getTopStyles(isFirst, isProgress, topColorFromProgress);
  const bottomColor = isLast ? "bg-transparent" : progressStyles.bottomColor;
  const cardMargin = getCardMargin(isProgress, isFirst, isLast);

  return {
    topColor: isFirst ? topColor : topColorFromProgress,
    bottomColor,
    iconBg: progressStyles.iconBg,
    iconDimensions: progressStyles.iconDimensions,
    topHeight: isFirst ? topHeight : topHeightFromProgress,
    cardMargin,
    resolvedType: progressStyles.resolvedType,
  };
};

export const TimelineItem = ({
  title,
  description,
  time,
  username,
  type = "circle",
  color,
  isCompleted = false,
  active = false,
  children,
  isFirst = false,
  isLast = false,
  isProgress = false,
  isDisabled = false,
  className = "",
  titleClassName = "",
  headerClassName = "",
  iconBackgroundColor = "",
}) => {
  const styles = getTimelineItemStyles({
    isProgress,
    isCompleted,
    isDisabled,
    isFirst,
    isLast,
    active,
    color,
    iconBackgroundColor,
  });

  const {
    topColor,
    bottomColor,
    iconBg,
    iconDimensions,
    topHeight,
    cardMargin,
    resolvedType,
  } = styles;

  const finalType = isProgress ? resolvedType : type;
  const sideTopCSS = `${topColor} ${topHeight}`;
  const { date, time: formattedTime } = formatDateAndTimeFromISO(time);
  return (
    <div className="flex gap-8 items-stretch">
      <div className="items-center flex flex-col justify-stretch">
        <div className={`w-0.5  ${sideTopCSS}`}></div>
        <div
          className={`flex items-center justify-center rounded-full ${iconBg} ${iconDimensions}`}
        >
          <IconType type={finalType} completed={isCompleted} color={color} />
        </div>
        <div className={`w-0.5 flex-1 ${bottomColor}`}></div>
      </div>
      {isProgress ? (
        <div className={`${cardMargin}`}>
          <h4 className="font-semibold text-[var(--color-neutral-5)]">{title}</h4>
          {description && (
            <p className="text-sm font-normal leading-5 mt-0.5" style={{ color: "rgba(255, 255, 255, 0.60)" }}>{description}</p>
          )}
          <p className="text-sm font-normal leading-5 mt-0.5" style={{ color: "rgba(255, 255, 255, 0.60)" }}>{children}</p>

          {time && (
            <>
              <hr className="border-[rgba(255,255,255,0.20)] mt-2 mb-1.5" />
              <DotSeprated
                left={date}
                right={formattedTime}
                className="!text-[rgba(255,255,255,0.60)] text-sm leading-5"
              />
            </>
          )}
        </div>
      ) : (
        <Card className={`bg-[var(--color-neutral-1)] flex-1 ${cardMargin}`}>
          <CardHeader className={headerClassName}>
            <div className="flex justify-between w-full">
              <div className="flex-1">
                <CardTitle className={titleClassName}>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
              <div className="flex flex-col">
                <DotSeprated
                  right={humanReadableDate(time)}
                  left={humanReadableTime(time)}
                />
                {username}
              </div>
            </div>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      )}
    </div>
  );
};
export const Timeline = ({
  children,
  isProgress = false,
  isDisabled = false,
  heading,
  heeadingClassname,
}) => {
  const count = React.Children.count(children);
  let activeCounted = false;
  let headingColor = "text-[var(--color-neutral-5)]";
  if (isProgress) {
    headingColor = "text-[var(--color-neutral-1)]";
  }
  const color = isDisabled ? "--color-neutral-1" : "--color-misc-progress";
   const childrenArray = React.Children.toArray(children);
   const hasActive = childrenArray.some(
    (child) => React.isValidElement(child) && child.props.active
  );
  return (
    <div className={`${!isProgress ? "p-3" : ""}`}>
      {heading && (
        <SectionHeader
          text={heading}
          headingColor={headingColor}
          className={heeadingClassname}
        />
      )}
      <div className="flex flex-col pt-3">
        {React.Children.map(children, (child, index) => {
          const isFirst = index === 0;
          const isLast = index === count - 1;
          const isCompleted = isProgress && hasActive && !activeCounted;
          if (!isDisabled && child?.props.active) {
            activeCounted = true;
          }

          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              isProgress,
              isFirst,
              isLast,
              isCompleted,
              color,
              isDisabled,
            });
          }
        })}
      </div>
    </div>
  );
};

IconType.propTypes = {
  type: PropTypes.oneOf([
    "completed",
    "started",
    "reminder",
    "watching",
    "upload",
    "file",
    "circle-filled",
    "circle",
  ]),
  color: PropTypes.string,
};

TimelineItem.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  time: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  username: PropTypes.string,
  type: PropTypes.oneOf([
    "completed",
    "started",
    "reminder",
    "watching",
    "upload",
    "file",
    "circle-filled",
    "circle",
  ]),
  color: PropTypes.string,
  isCompleted: PropTypes.bool,
  active: PropTypes.bool,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  isProgress: PropTypes.bool,
  isDisabled: PropTypes.bool,
  children: PropTypes.node,
};

Timeline.propTypes = {
  children: PropTypes.node,
  isProgress: PropTypes.bool,
  isDisabled: PropTypes.bool,
  heading: PropTypes.string,
};
